import React, { createRef, forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import { arrayOf, shape, number, string, func, oneOfType, array, oneOf, bool } from 'prop-types';
import { useTranslation } from 'react-i18next';
import FormCheckbox from '../../atoms/FormCheckbox/index.jsx';
import FormInput from '../../atoms/FormInput/index.jsx';
import { useBreakpoints, useOnClickOutside, useKeyboardTabbed } from '../../hooks/index.js';
import Modal from '../Modal/index.jsx';
import FormContext from '../../context/FormContext.js';
import FormGroupContext from '../../context/FormGroupContext.js';
import { filterSupportedEventListenerOptions, throttle } from '../../utils/index.js';

// An internal key for the optional "Select All" checkbox. The leading
// underscore is there to help avoid clashes with "real" option values.
const SELECT_ALL_VALUE = '_select-all';
const parseArray = value => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return JSON.parse(value);
};

/**
 * FormMultiSelect component.
 */
var _ref = /*#__PURE__*/React.createElement("span", {
  className: "form-multi-select__caret-icon p-absolute"
});
const FormMultiSelect = /*#__PURE__*/forwardRef(({
  className,
  name,
  label,
  defaultValue,
  includeSelectAll,
  options: initialOptions,
  onChange,
  position,
  selectAllLabel,
  value,
  tabIndex,
  showInlineLabel,
  showModalSearchbox
}, ref) => {
  const enableFocusStyles = useKeyboardTabbed();
  const {
    inputId,
    feedbackId
  } = useContext(FormGroupContext);
  const {
    errors,
    onChange: onFormChange,
    defaultValues
  } = useContext(FormContext);
  const {
    t
  } = useTranslation();
  const selectAllText = selectAllLabel || t('molecules.formMultiSelect.filterOptions.selectAllLabel');
  const handleChange = evt => {
    if (onChange) {
      onChange(evt);
    }
    if (onFormChange) {
      onFormChange(evt);
    }
  };
  const hiddenInputRef = useRef(ref);
  const searchInputRef = useRef();
  const dropdownRef = useRef();
  const wrapperRef = useRef();
  const checkboxRefs = useRef([]);
  const [showModal, setShowModal] = useState(false);
  const [options, setOptions] = useState(initialOptions);
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // Use value for a controlled and defaultValue for an uncontrolled element.
  // https://reactjs.org/docs/forms.html#controlled-components
  const finalValues = parseArray(value || defaultValue || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name]));

  // Let the value be passed as a JSON-stringified or regular array.
  const [checkedValues, setCheckedValues] = useState(finalValues);

  // Update checked values if a new value is passed in.
  useEffect(() => {
    setCheckedValues(finalValues);
  }, [JSON.stringify(parseArray(value))]);
  const {
    isAboveBreakpoint
  } = useBreakpoints();
  const isModalView = !isAboveBreakpoint('sm', false);

  // Filter the available options according to what has been typed in the
  // input box.
  const filteredOptions = options.filter(option => {
    const isFiltered = !inputValue || option.label.toLowerCase().includes(inputValue.toLowerCase());

    // If the selected option has been filtered out deselect it.
    if (!isFiltered && option === selectedOption) {
      setSelectedOption(null);
    }
    return isFiltered;
  });
  const allChecked = checkedValues.length === options.length;
  const noneChecked = !checkedValues.length;
  if (includeSelectAll) {
    filteredOptions.unshift({
      label: selectAllText,
      value: SELECT_ALL_VALUE,
      indeterminate: !allChecked && !noneChecked
    });
  }

  // Update refs if filtered options change.
  useEffect(() => {
    checkboxRefs.current = options.map((_, i) => {
      var _checkboxRefs$current;
      return (_checkboxRefs$current = checkboxRefs.current[i]) !== null && _checkboxRefs$current !== void 0 ? _checkboxRefs$current : /*#__PURE__*/createRef();
    });
  }, [filteredOptions]);

  // Update options if changed.
  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  /**
   * Position the dropdown above or below the input, depending on what looks best.
   */
  const positionDropdown = () => {
    const dropdownEl = dropdownRef.current;
    const inputEl = searchInputRef.current;
    const originalDisplay = dropdownEl.style.display;

    // In case the dropdown is collapsed display it so we can grap the height.
    dropdownEl.style.display = 'flex';
    const {
      height: dropdownHeight
    } = dropdownEl.getBoundingClientRect();
    const {
      y: inputY,
      height: inputHeight
    } = inputEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - (inputY + inputHeight);
    const spaceAbove = inputY;
    const fitsBelow = spaceBelow >= dropdownHeight;

    // Reset the original display value so it doesn't actually render.
    dropdownEl.style.display = originalDisplay;
    const aboveTransform = `translateY(-${dropdownHeight + inputHeight}px)`;
    const belowTransform = 'translateY(0px)';
    if (position === 'above') {
      dropdownEl.style.transform = aboveTransform;
      return;
    }
    if (position === 'below') {
      dropdownEl.style.transform = belowTransform;
      return;
    }

    // Auto
    dropdownEl.style.transform = !fitsBelow && spaceAbove > spaceBelow ? aboveTransform : belowTransform;
  };

  /**
   * Get a unique ID for the given option.
   */
  const getOptionId = option => [inputId || name, option === null || option === void 0 ? void 0 : option.value].filter(x => x).join('-') || null;

  // Scroll to the selected option within the dropdown for the case where the
  // list is long and we're keyboard navigating.
  useEffect(() => {
    if (!selectedOption) {
      return;
    }
    const parentRect = dropdownRef.current.getBoundingClientRect();
    const optionId = getOptionId(selectedOption);
    const listItem = document.getElementById(optionId);
    const childRect = listItem.getBoundingClientRect();
    const isViewable = childRect.top >= parentRect.top && childRect.bottom <= parentRect.top + parentRect.height;
    if (!isViewable) {
      const scrollTop = childRect.top - parentRect.top;
      const scrollBottom = childRect.bottom - parentRect.bottom;

      // Align the child element to the top or bottom of the container depending
      // on which is closer.
      if (Math.abs(scrollTop) < Math.abs(scrollBottom)) {
        dropdownRef.current.scrollTop += scrollTop;
        return;
      }
      dropdownRef.current.scrollTop += scrollBottom;
    }
  }, [selectedOption, dropdownRef]);

  /**
   * Open the dropdown, positioning it first.
   */
  const toggleDropdown = show => {
    if (show) {
      positionDropdown();
    }
    setShowDropdown(show);
  };

  /**
   * Fire an input event manually.
   */
  const fireInputEvent = newCheckedValues => {
    if (!hiddenInputRef.current) {
      return;
    }

    // The value is set directly here so that it is correct when the event is
    // picked up.
    hiddenInputRef.current.value = JSON.stringify(newCheckedValues);
    hiddenInputRef.current.dispatchEvent(new Event('input', {
      bubbles: true
    }));
  };

  /**
   * Handle a change of text in the search input.
   */
  const onTextInputChange = evt => {
    evt.preventDefault();
    setInputValue(evt.target.value);

    // Show the dropdown if something was entered in the search box.
    if (evt.target.value) {
      setShowDropdown(true);
    }
  };

  /**
   * Handle a key down event on the search input.
   *
   * See https://www.w3.org/TR/wai-aria-1.1/#combobox
   */
  const onTextInputKeyDown = evt => {
    const {
      keyCode
    } = evt;

    // Open the dropdown when the down key is pressed, if not already open.
    if (!showDropdown && keyCode === 40) {
      toggleDropdown(true);
      setSelectedOption(filteredOptions[0]);
      return;
    }
    const selectedIndex = filteredOptions.findIndex(opt => opt.value === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value));
    const lastIndex = filteredOptions.length - 1;

    // Move to the next item when the down key is pressed.
    if (keyCode === 40) {
      const nextIndex = selectedIndex === lastIndex ? selectedIndex : selectedIndex + 1;
      setSelectedOption(filteredOptions[nextIndex]);
      return;
    }

    // Move to the previous item when the up key is pressed.
    if (keyCode === 38) {
      const prevIndex = selectedIndex === 0 ? selectedIndex : selectedIndex - 1;
      setSelectedOption(filteredOptions[prevIndex]);
      return;
    }

    // Move to the first item when the home key is pressed.
    if (keyCode === 36) {
      setSelectedOption(filteredOptions[0]);
      return;
    }

    // Move to the last item when the end key is pressed.
    if (keyCode === 35) {
      setSelectedOption(filteredOptions[lastIndex]);
      return;
    }

    // Select an option when the enter key is pressed.
    if (keyCode === 13) {
      checkboxRefs.current[selectedIndex].current.click();
      setSelectedOption(null);
      return;
    }

    // Close the dropdown when the esc or tab keys are pressed.
    if (keyCode === 27 || keyCode === 9) {
      toggleDropdown(false);
      setShowModal(false);
      setInputValue('');
      setSelectedOption(null);
    }
  };

  /**
   * Handle a click on the search input.
   */
  const toggleCombobox = () => {
    if (isModalView) {
      setShowModal(true);
      setInputValue('');
      return;
    }
    toggleDropdown(!showDropdown);
  };

  /**
   * Handle an option being checked or unchecked.
   */
  const onCheckboxChange = evt => {
    const {
      target
    } = evt;
    const allValues = options.map(option => option.value);
    const selectAllValues = allChecked ? [] : allValues;
    if (target.value === SELECT_ALL_VALUE) {
      setCheckedValues(selectAllValues);
      fireInputEvent(selectAllValues);
      return;
    }
    const newCheckedValues = [...checkedValues].filter(val => String(val) !== target.value);
    if (target.checked) {
      newCheckedValues.push(target.value);
    }
    setCheckedValues(newCheckedValues);
    fireInputEvent(newCheckedValues);
  };

  /**
   * Check if a checkbox is checked.
   */
  const isChecked = option => {
    if (option.value === SELECT_ALL_VALUE) {
      return allChecked;
    }
    return checkedValues.includes(String(option.value));
  };

  // Hide the dropdown and modal display if a screen resize crosses the
  // relevant breakpoint.
  useEffect(() => {
    toggleDropdown(false);
    setShowModal(false);
    setInputValue('');
  }, [isModalView]);

  // Hide on click outside the dropdown.
  useOnClickOutside(wrapperRef, () => {
    if (!showDropdown) {
      return;
    }
    toggleDropdown(false);
  }, [showDropdown]);

  // When open, reposition the dropdown on scroll or resize.
  useEffect(() => {
    if (!showDropdown) {
      return () => {};
    }
    const handleScrollOrResize = throttle(positionDropdown, 100);
    const evtOpts = filterSupportedEventListenerOptions({
      passive: true
    });
    window.addEventListener('scroll', handleScrollOrResize, evtOpts);
    window.addEventListener('resize', handleScrollOrResize, evtOpts);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showDropdown]);
  const listBoxId = `${inputId || name}-listbox`;
  const contextValue = useMemo(() => ({}), []);
  const list = /*#__PURE__*/React.createElement("ul", {
    id: listBoxId,
    className: "ma-reset pa-reset",
    role: "listbox",
    "aria-label": "options"
  }, filteredOptions.map((option, i) => {
    const isSelected = option.value === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value);
    return /*#__PURE__*/React.createElement("li", {
      id: getOptionId(option),
      key: option.value,
      className: "form-multi-select__list-item d-flex" + (isSelected ? " form-multi-select__list-item--selected" : ""),
      role: "option",
      "aria-selected": isSelected,
      tabIndex: "-1",
      "data-item-name": option.label
    }, /*#__PURE__*/React.createElement(FormGroupContext.Provider, {
      value: contextValue
    }, /*#__PURE__*/React.createElement(FormCheckbox, {
      fluid: true,
      rightToLeft: true,
      ref: checkboxRefs.current[i],
      className: "form-multi-select__list-item-inner align-items-center",
      label: option.count ? `${option.label} (${option.count})` : option.label,
      indeterminate: option.indeterminate,
      onChange: onCheckboxChange,
      value: option.value,
      checked: isChecked(option),
      tabIndex: "-1"
    })));
  }), !filteredOptions.length && /*#__PURE__*/React.createElement("li", {
    className: "form-multi-select__list-item form-multi-select__no-options-item body-copy-small"
  }, /*#__PURE__*/React.createElement("span", {
    className: "form-multi-select__list-item-inner"
  }, t('molecules.formMultiSelect.filterOptions.noResult'))));
  return /*#__PURE__*/React.createElement("div", {
    className: cn('form-multi-select', className, showDropdown && 'form-multi-select--open', enableFocusStyles && 'form-multi-select--enable-focus-styles'),
    "aria-expanded": !!(showModal || showDropdown)
    // Our current version of jsx-a11y claims a combobox must have an
    // aria-controls attribute, which is not the case according to
    // https://www.w3.org/TR/wai-aria-1.1/#combobox
    // (the inner textbox/searchbox has the aria-controls attribute).
    //
    // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
    ,
    role: "combobox",
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: name,
    value: JSON.stringify(checkedValues),
    onChange: () => {} // noop
    ,
    ref: hiddenInputRef,
    onInput: handleChange,
    "aria-describedby": feedbackId
  }), !(showModalSearchbox && showModal) && /*#__PURE__*/React.createElement(FormInput, {
    className: "form-multi-select__searchbox",
    role: "searchbox",
    name: `${name}:searchbox`,
    hasError: !!(errors === null || errors === void 0 ? void 0 : errors[name]),
    "aria-haspopup": "listbox",
    "aria-controls": listBoxId,
    "aria-activedescendant": getOptionId(selectedOption),
    "aria-label": t('molecules.formMultiSelect.ariaLabel.formInput', {
      label
    }),
    ref: searchInputRef,
    onChange: onTextInputChange,
    onClick: toggleCombobox,
    onKeyDown: onTextInputKeyDown,
    placeholder: checkedValues.length ? `${checkedValues.length} ${t('molecules.formMultiSelect.filterOptions.inputPlaceholderSelected')}` : t('molecules.formMultiSelect.filterOptions.inputPlaceholderSelect'),
    autoComplete: "off",
    tabIndex: tabIndex,
    inlineLabel: showInlineLabel ? label : null,
    inlineLabelSelected: !!checkedValues.length
  }), /*#__PURE__*/React.createElement("button", {
    className: "form-multi-select__caret-button p-absolute",
    onClick: toggleCombobox,
    type: "button",
    tabIndex: tabIndex,
    "data-component": "Button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, t('molecules.formMultiSelect.sr.optionsButton')), _ref), /*#__PURE__*/React.createElement("div", {
    ref: dropdownRef,
    className: "form-multi-select__options"
  }, !isModalView && list), /*#__PURE__*/React.createElement(Modal, {
    show: showModal,
    onClose: () => setShowModal(false)
  }, /*#__PURE__*/React.createElement(Modal.Header, null, label), showModalSearchbox && showModal && /*#__PURE__*/React.createElement("div", {
    className: "form-multi-select__modal-searchbox"
  }, /*#__PURE__*/React.createElement(FormInput, {
    role: "searchbox",
    name: `${name}:searchbox`,
    hasError: !!(errors === null || errors === void 0 ? void 0 : errors[name]),
    "aria-haspopup": "listbox",
    "aria-controls": listBoxId,
    "aria-activedescendant": getOptionId(selectedOption),
    "aria-label": `Filter ${label}`,
    ref: searchInputRef,
    onChange: onTextInputChange,
    onClick: toggleCombobox,
    onKeyDown: onTextInputKeyDown,
    placeholder: checkedValues.length ? `${checkedValues.length} selected` : 'Select...',
    autoComplete: "off",
    tabIndex: tabIndex,
    inlineLabel: "Refine list...",
    inlineLabelSelected: !!checkedValues.length
  })), /*#__PURE__*/React.createElement(Modal.Body, null, isModalView && list)));
});
FormMultiSelect.displayName = 'FormMultiSelect';
FormMultiSelect.defaultProps = {
  className: null,
  options: [],
  onChange: null,
  includeSelectAll: false,
  defaultValue: null,
  position: 'auto',
  selectAllLabel: null,
  value: null,
  tabIndex: null,
  showInlineLabel: false,
  showModalSearchbox: false
};
FormMultiSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** A name for the input. */
  name: string.isRequired,
  /** Include a "Select All" input as the first option. */
  includeSelectAll: bool,
  /** A label for the input (used in the modal). */
  label: string.isRequired,
  /** The selectable options. */
  options: arrayOf(shape({
    /** The value of the item. */
    value: oneOfType([number, string]),
    /** A label for the item. */
    label: string
  })),
  /** On change event handler. */
  onChange: func,
  /** The default value for the input field, as comma separated filter values. */
  defaultValue: oneOfType([string, array]),
  /** The position of the expanded dropdown. */
  position: oneOf(['above', 'below', 'auto']),
  /** An alternative label for the select all option (if used). */
  selectAllLabel: string,
  /** The value for when using as a controlled component. */
  value: oneOfType([string, array]),
  /** Sets whether the input is accessible via keyboard navigation */
  tabIndex: string,
  /** Sets whether the inline label is used */
  showInlineLabel: bool,
  /** Sets whether the mobile modal should have search box */
  showModalSearchbox: bool
} : {};
export default FormMultiSelect;